import type {
  Schemas,
  V2EpisodeDetail,
} from "@softmax/observatory-client/types";
import { buildHref, type GameAddress } from "@softmax/web-addresses";
import {
  resourceHref,
  type ResourceDestination,
  type ReplayInput,
} from "./resource-search";
import {
  readResource,
  canonicalizeResource,
  type ResourceViewer,
} from "./resource-reader";

export type ReplayPageData = {
  viewer: ResourceViewer;
} & (
  | {
      status: "unavailable";
      coworldId: string;
      address: GameAddress | null;
    }
  | {
      status: "ready";
      replay: Schemas["CoworldReplayAddressResponse"];
      game: string | null;
      gameVersion: string | null;
      achievement: string | null;
    }
);

export async function loadReplay({
  signal,
  data: input,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly data: ReplayInput;
}): Promise<ReplayPageData> {
  const value = (name: string) => {
    const entry = input.search[name];
    return typeof entry === "string" ? entry : null;
  };
  let coworldId =
    input.target === "coworld" ? input.resourceId : value("coworld_id");
  let replayUri = value("replay_uri");
  let game = value("game");
  let gameVersion = value("game_version");
  const episodeId =
    input.target === "episode" ? input.resourceId : value("episode_id");
  let destination: ResourceDestination;

  if (input.target === "episode") {
    destination = {
      name: "episodeReplay",
      episodeId: input.resourceId,
      game: input.game,
    };
  } else {
    destination = { name: "replay", coworldId: input.resourceId };
  }

  const returnHref = resourceHref({
    input,
    destination,
  });

  if (input.target === "episode" && (!coworldId || !replayUri)) {
    const { resource: episode, viewer } = await readResource<V2EpisodeDetail>({
      signal,
      path: `/v2/episodes/${encodeURIComponent(input.resourceId)}`,
      returnHref,
    });
    coworldId = episode.coworld_id;
    replayUri = episode.replay_url;
    game = episode.coworld_name;
    gameVersion = episode.coworld_version;

    if (!coworldId || !replayUri) {
      const canonical = episodeReplayHref(episode);
      canonicalizeResource({ input, destination, canonical });
      return {
        status: "unavailable",
        coworldId: coworldId ?? "",
        address: episode.game_address ?? null,
        viewer,
      };
    }
  }

  if (!coworldId || !replayUri || !episodeId) {
    return {
      status: "unavailable",
      coworldId: coworldId ?? "",
      address: null,
      viewer: null,
    };
  }

  const parameters = new URLSearchParams({
    coworld_id: coworldId,
    replay_uri: replayUri,
    episode_id: episodeId,
  });
  const { resource: replay, viewer } = await readResource<
    Schemas["CoworldReplayAddressResponse"]
  >({
    signal,
    path: `/v2/coworlds/replays/address?${parameters}`,
    returnHref,
  });
  const canonical = replay.game_address
    ? buildHref({
        name: "episodeReplay",
        game: replay.game_address,
        episodeId: replay.episode_id,
      })
    : buildHref({ name: "replay", coworldId: replay.coworld_id });
  canonicalizeResource({
    input,
    destination,
    canonical,
    search: {
      ...input.search,
      coworld_id: replay.coworld_id,
      episode_id: replay.episode_id,
      replay_uri: replay.replay_uri,
    },
  });
  return {
    status: "ready",
    replay,
    game,
    gameVersion,
    achievement: value("achievement"),
    viewer,
  };
}

function episodeReplayHref(episode: V2EpisodeDetail): string {
  return episode.game_address
    ? buildHref({
        name: "episodeReplay",
        game: episode.game_address,
        episodeId: episode.id,
      })
    : buildHref({ name: "episode", episodeId: episode.id });
}
